import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

// ── NSV PLATFORM MAINTENANCE CHECKLIST ───────────────────────
// Nancy's master platform health system
// Two views: by Frequency (default) and by Category
// All checks save to Supabase with timestamps

const MAINTENANCE_ITEMS = [
  // ── DAILY ────────────────────────────────────────────────────
  { id:'d1',  freq:'daily',     cat:'technical',     icon:'🖥️',  title:'Review server & API error logs',               desc:'Check Netlify function logs, Supabase logs, and any webhook errors.' },
  { id:'d2',  freq:'daily',     cat:'technical',     icon:'📡',  title:'Check uptime monitoring alerts',               desc:'Verify site is live and all endpoints responding.' },
  { id:'d3',  freq:'daily',     cat:'operations',    icon:'📧',  title:'Check support inbox',                          desc:'Review and respond to notary/client support emails.' },
  { id:'d4',  freq:'daily',     cat:'financial',     icon:'💳',  title:'Review new transactions',                      desc:'Check Stripe dashboard for any failed payments or disputes.' },
  { id:'d5',  freq:'daily',     cat:'notary',        icon:'👥',  title:'Check pending notary approvals',               desc:'Review any new notary applications awaiting approval.' },

  // ── WEEKLY ───────────────────────────────────────────────────
  { id:'w1',  freq:'weekly',    cat:'technical',     icon:'🗄️',  title:'Database health check',                        desc:'Review Supabase storage usage, slow queries, and table sizes.' },
  { id:'w2',  freq:'weekly',    cat:'workflow',      icon:'📨',  title:'Email template routing audit',                 desc:'Send test emails to verify correct routing and content for all templates.' },
  { id:'w3',  freq:'weekly',    cat:'workflow',      icon:'🔔',  title:'Notification logic test',                      desc:'Verify SMS and email alerts are firing correctly for key status changes.' },
  { id:'w4',  freq:'weekly',    cat:'workflow',      icon:'🔗',  title:'Webhook reliability check',                    desc:'Confirm Stripe and other webhooks are delivering and retrying correctly.' },
  { id:'w5',  freq:'weekly',    cat:'notary',        icon:'⭐',  title:'Review notary performance ratings',            desc:'Check new ratings, flag any notaries with declining scores.' },
  { id:'w6',  freq:'weekly',    cat:'financial',     icon:'📊',  title:'Payment reconciliation',                       desc:'Verify all completed jobs are matched to payments in Stripe.' },
  { id:'w7',  freq:'weekly',    cat:'operations',    icon:'📈',  title:'Analytics review',                             desc:'Check traffic, conversions, and drop-off points in your analytics tool.' },

  // ── MONTHLY ──────────────────────────────────────────────────
  { id:'m1',  freq:'monthly',   cat:'technical',     icon:'🔒',  title:'SSL certificate check',                        desc:'Verify SSL cert expiration dates and auto-renewal is working.' },
  { id:'m2',  freq:'monthly',   cat:'technical',     icon:'💾',  title:'Test backup restoration',                      desc:'Restore a test backup to verify backups are working correctly.' },
  { id:'m3',  freq:'monthly',   cat:'frontend',      icon:'🔗',  title:'Broken link scan',                             desc:'Crawl all pages for 404s and broken internal/external links.' },
  { id:'m4',  freq:'monthly',   cat:'frontend',      icon:'📱',  title:'Mobile responsiveness audit',                  desc:'Test all portal flows on iPhone and Android devices.' },
  { id:'m5',  freq:'monthly',   cat:'frontend',      icon:'🏎️',  title:'Page speed optimization check',                desc:'Run Lighthouse or PageSpeed Insights — target 90+ score.' },
  { id:'m6',  freq:'monthly',   cat:'workflow',      icon:'⚙️',  title:'Dashboard status logic test',                  desc:'Walk through all 10 job statuses and verify correct display in all portals.' },
  { id:'m7',  freq:'monthly',   cat:'security',      icon:'🛡️',  title:'Access control audit',                         desc:'Review admin, notary, and client roles — remove any stale accounts.' },
  { id:'m8',  freq:'monthly',   cat:'notary',        icon:'📜',  title:'Credential expiration review',                 desc:'Check all notary credentials for upcoming expirations (30-day warning).' },
  { id:'m9',  freq:'monthly',   cat:'content',       icon:'📚',  title:'Resource library review',                      desc:'Update any outdated guides, forms, or training materials.' },
  { id:'m10', freq:'monthly',   cat:'financial',     icon:'🧾',  title:'Invoice template accuracy check',              desc:'Verify invoice branding, line items, and totals are displaying correctly.' },
  { id:'m11', freq:'monthly',   cat:'operations',    icon:'📬',  title:'Email domain health check',                    desc:'Verify SPF, DKIM, and DMARC records are passing. Check sender reputation.' },
  { id:'m12', freq:'monthly',   cat:'notary',        icon:'😴',  title:'Inactive notary review',                       desc:'Identify notaries with no activity in 60+ days — re-engage or archive.' },

  // ── QUARTERLY ────────────────────────────────────────────────
  { id:'q1',  freq:'quarterly', cat:'technical',     icon:'📦',  title:'Security patching',                            desc:'Update all npm dependencies, libraries, and frameworks. Test after update.' },
  { id:'q2',  freq:'quarterly', cat:'technical',     icon:'🏋️',  title:'Load testing before major releases',           desc:'Simulate high traffic to verify platform stability under load.' },
  { id:'q3',  freq:'quarterly', cat:'frontend',      icon:'♿',  title:'ADA compliance check',                         desc:'Verify contrast ratios, alt text, keyboard navigation, and ARIA labels.' },
  { id:'q4',  freq:'quarterly', cat:'frontend',      icon:'🌐',  title:'Cross-browser testing',                        desc:'Test all flows in Chrome, Safari, Edge, and mobile browsers.' },
  { id:'q5',  freq:'quarterly', cat:'security',      icon:'🔑',  title:'API key rotation',                             desc:'Rotate Supabase, Resend, Twilio, and Stripe API keys. Update env vars.' },
  { id:'q6',  freq:'quarterly', cat:'security',      icon:'🚨',  title:'Incident response plan review',                desc:'Review and update your incident response playbook.' },
  { id:'q7',  freq:'quarterly', cat:'security',      icon:'📋',  title:'Data retention policy review',                 desc:'Verify documents, logs, and PII are being retained per policy.' },
  { id:'q8',  freq:'quarterly', cat:'compliance',    icon:'🗺️',  title:'RON compliance state updates',                 desc:'Review state-by-state RON law changes. Update Resources section.' },
  { id:'q9',  freq:'quarterly', cat:'content',       icon:'❓',  title:'FAQ + Help Center updates',                    desc:'Update FAQs based on recent support tickets and common questions.' },
  { id:'q10', freq:'quarterly', cat:'operations',    icon:'🔭',  title:'Competitor scan',                              desc:'Review Jot, NotaryDash, and SigningOrder for new features and pricing.' },
  { id:'q11', freq:'quarterly', cat:'notary',        icon:'📖',  title:'Training material updates',                    desc:'Refresh Academy content, update Training Manual, review glossary.' },
  { id:'q12', freq:'quarterly', cat:'admin',         icon:'📝',  title:'Internal documentation update',                desc:'Update SOPs, workflows, and developer notes to reflect current state.' },
  { id:'q13', freq:'quarterly', cat:'growth',        icon:'🔍',  title:'SEO audit',                                    desc:'Review metadata, schema markup, and backlink profile.' },
  { id:'q14', freq:'quarterly', cat:'growth',        icon:'🔥',  title:'Heatmap & UX review',                          desc:'Review user behavior data — identify friction points and drop-offs.' },
  { id:'q15', freq:'quarterly', cat:'financial',     icon:'✅',  title:'Refund workflow test',                          desc:'Test the full refund flow in Stripe — ensure clean and compliant.' },

  // ── YEARLY ───────────────────────────────────────────────────
  { id:'y1',  freq:'yearly',    cat:'security',      icon:'🔐',  title:'Full security audit',                          desc:'Comprehensive review of encryption, auth, permissions, and data handling.' },
  { id:'y2',  freq:'yearly',    cat:'compliance',    icon:'⚖️',  title:'Full compliance review',                       desc:'Review all notary agreements, contractor terms, privacy policy, and TOS.' },
  { id:'y3',  freq:'yearly',    cat:'technical',     icon:'🏗️',  title:'Architecture review',                          desc:'Evaluate platform architecture for scalability as NSV grows.' },
  { id:'y4',  freq:'yearly',    cat:'frontend',      icon:'🎨',  title:'Full UX/brand audit',                          desc:'Review all portals for brand consistency, tone, ADA, and mobile UX.' },
  { id:'y5',  freq:'yearly',    cat:'operations',    icon:'📣',  title:'Annual notary network review',                 desc:'Full performance review of all active notaries. Update tiering.' },
  { id:'y6',  freq:'yearly',    cat:'growth',        icon:'🗺️',  title:'Annual growth strategy review',                desc:'Review HTDL, prioritize roadmap, set targets for next 12 months.' },
  { id:'y7',  freq:'yearly',    cat:'financial',     icon:'📑',  title:'Full transaction log audit',                   desc:'Annual review of all transactions — check for mismatches, duplicates, failures.' },
  { id:'y8',  freq:'yearly',    cat:'admin',         icon:'🧹',  title:'Full platform cleanup',                        desc:'Archive old jobs, clean up test data, remove unused assets and code.' },
]

const FREQUENCIES = [
  { id:'daily',     label:'Daily',     icon:'☀️',  color:'#185FA5', bg:'#EBF4FF' },
  { id:'weekly',    label:'Weekly',    icon:'📅',  color:'#5E8A72', bg:'#EAF2EE' },
  { id:'monthly',   label:'Monthly',   icon:'🗓️',  color:'#41427a', bg:'#EEF0F8' },
  { id:'quarterly', label:'Quarterly', icon:'📊',  color:'#4D4E8D', bg:'#F0EBF8' },
  { id:'yearly',    label:'Yearly',    icon:'🏆',  color:'#151715', bg:'#E2E4E7' },
]

const CATEGORIES = [
  { id:'technical',  label:'Technical',      icon:'🖥️'  },
  { id:'frontend',   label:'Frontend & UX',  icon:'🎨'  },
  { id:'workflow',   label:'Workflow',        icon:'⚙️'  },
  { id:'security',   label:'Security',        icon:'🔒'  },
  { id:'compliance', label:'Compliance',      icon:'⚖️'  },
  { id:'content',    label:'Content',         icon:'📚'  },
  { id:'operations', label:'Operations',      icon:'📋'  },
  { id:'notary',     label:'Notary Network',  icon:'👥'  },
  { id:'admin',      label:'Admin & Internal',icon:'🛠️'  },
  { id:'financial',  label:'Financial',       icon:'💰'  },
  { id:'growth',     label:'Growth',          icon:'🚀'  },
]

export default function PlatformMaintenance() {
  const { user } = useAuth()
  const [checks,    setChecks]    = useState({})   // { itemId: { done: true, at: isoString } }
  const [view,      setView]      = useState('frequency') // 'frequency' | 'category'
  const [expanded,  setExpanded]  = useState({ daily: true, weekly: true, monthly: false, quarterly: false, yearly: false })
  const [saving,    setSaving]    = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    if (!user) return
    supabase.from('profiles').select('maintenance_checks').eq('id', user.id).single()
      .then(({ data }) => {
        if (data?.maintenance_checks) setChecks(data.maintenance_checks)
        setLoading(false)
      })
  }, [user])

  async function toggleCheck(itemId) {
    const already = checks[itemId]?.done
    const newChecks = {
      ...checks,
      [itemId]: already
        ? { done: false, at: null }
        : { done: true,  at: new Date().toISOString() },
    }
    setChecks(newChecks)
    setSaving(true)
    try {
      await supabase.from('profiles').update({ maintenance_checks: newChecks }).eq('id', user.id)
      setLastSaved(new Date())
    } catch (err) {
      toast.error('Could not save: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  async function resetPeriod(freq) {
    if (!window.confirm(`Reset all ${freq} checks? This will clear their completion status.`)) return
    const itemIds = MAINTENANCE_ITEMS.filter(i => i.freq === freq).map(i => i.id)
    const newChecks = { ...checks }
    itemIds.forEach(id => { newChecks[id] = { done: false, at: null } })
    setChecks(newChecks)
    await supabase.from('profiles').update({ maintenance_checks: newChecks }).eq('id', user.id)
    toast.success(`${freq.charAt(0).toUpperCase() + freq.slice(1)} checks reset!!`)
  }

  function getFreqProgress(freq) {
    const items = MAINTENANCE_ITEMS.filter(i => i.freq === freq)
    const done  = items.filter(i => checks[i.id]?.done).length
    return { done, total: items.length, pct: Math.round((done / items.length) * 100) }
  }

  function getCatProgress(cat) {
    const items = MAINTENANCE_ITEMS.filter(i => i.cat === cat)
    const done  = items.filter(i => checks[i.id]?.done).length
    return { done, total: items.length, pct: Math.round((done / items.length) * 100) }
  }

  function getTotalProgress() {
    const done  = MAINTENANCE_ITEMS.filter(i => checks[i.id]?.done).length
    const total = MAINTENANCE_ITEMS.length
    return { done, total, pct: Math.round((done / total) * 100) }
  }

  // ── SINGLE CHECK ITEM ──────────────────────────────────────
  function CheckItem({ item }) {
    const done = checks[item.id]?.done
    const at   = checks[item.id]?.at
    return (
      <div
        onClick={() => toggleCheck(item.id)}
        style={{
          display:      'flex',
          alignItems:   'flex-start',
          gap:          10,
          padding:      '10px 14px',
          borderRadius: 8,
          cursor:       'pointer',
          background:   done ? '#F0FAF4' : '#F7F8F9',
          border:       `1px solid ${done ? 'rgba(45,122,79,0.2)' : '#EEEFF1'}`,
          marginBottom: 4,
          transition:   'all 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#4D4E8D'}
        onMouseLeave={e => e.currentTarget.style.borderColor = done ? 'rgba(45,122,79,0.2)' : '#EEEFF1'}>
        {/* Checkbox */}
        <div style={{
          width:          20,
          height:         20,
          borderRadius:   4,
          border:         `2px solid ${done ? '#5E8A72' : '#C9C5BA'}`,
          background:     done ? '#5E8A72' : '#fff',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          flexShrink:     0,
          marginTop:      1,
          transition:     'all 0.15s',
        }}>
          {done && <span style={{ color:'#fff', fontSize:12, fontWeight:700, lineHeight:1 }}>✓</span>}
        </div>
        {/* Content */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:14 }}>{item.icon}</span>
            <span style={{
              fontSize:        13,
              fontWeight:      500,
              color:           done ? '#5E8A72' : '#151715',
              textDecoration:  done ? 'line-through' : 'none',
              opacity:         done ? 0.7 : 1,
            }}>{item.title}</span>
          </div>
          <div style={{ fontSize:11, color:'#5C6065', marginTop:2, lineHeight:1.4 }}>{item.desc}</div>
          {done && at && (
            <div style={{ fontSize:10, color:'#5E8A72', marginTop:3, fontWeight:500 }}>
              ✓ Completed {new Date(at).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })} at {new Date(at).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' })}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── FREQUENCY SECTION ─────────────────────────────────────
  function FreqSection({ freq }) {
    const f    = FREQUENCIES.find(f => f.id === freq)
    const prog = getFreqProgress(freq)
    const open = expanded[freq]
    const items = MAINTENANCE_ITEMS.filter(i => i.freq === freq)

    return (
      <div style={{
        background:   '#fff',
        border:       `1.5px solid ${prog.pct === 100 ? '#5E8A72' : '#E2E4E7'}`,
        borderRadius: 12,
        overflow:     'hidden',
        marginBottom: 10,
      }}>
        {/* Header */}
        <div
          onClick={() => setExpanded(e => ({ ...e, [freq]: !e[freq] }))}
          style={{
            display:    'flex',
            alignItems: 'center',
            gap:        12,
            padding:    '14px 16px',
            cursor:     'pointer',
            background: open ? f.bg : '#fff',
            transition: 'background 0.15s',
          }}>
          <div style={{ fontSize:22 }}>{f.icon}</div>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
              <span style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:17, fontWeight:600, color:'#151715' }}>{f.label} Tasks</span>
              <span style={{ fontSize:10, background: prog.pct === 100 ? '#EAF2EE' : f.bg, color: prog.pct === 100 ? '#5E8A72' : f.color, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>
                {prog.done}/{prog.total} {prog.pct === 100 ? '✅' : ''}
              </span>
            </div>
            <div style={{ height:3, background:'#E2E4E7', borderRadius:2, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${prog.pct}%`, background:`linear-gradient(90deg,${f.color},${f.color}cc)`, borderRadius:2, transition:'width 0.4s ease' }} />
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {prog.done > 0 && prog.pct < 100 && (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); resetPeriod(freq) }}
                style={{ fontSize:10, color:'#A2A6AA', background:'none', border:'1px solid #D7DADE', borderRadius:20, padding:'2px 8px', cursor:'pointer', fontFamily:'inherit' }}>
                Reset
              </button>
            )}
            <span style={{ color:'#A2A6AA', fontSize:12 }}>{open ? '▲' : '▼'}</span>
          </div>
        </div>

        {/* Items */}
        {open && (
          <div style={{ padding:'8px 12px 12px' }}>
            {items.map(item => <CheckItem key={item.id} item={item} />)}
          </div>
        )}
      </div>
    )
  }

  // ── CATEGORY SECTION ──────────────────────────────────────
  function CatSection({ cat }) {
    const c     = CATEGORIES.find(c => c.id === cat)
    const prog  = getCatProgress(cat)
    const items = MAINTENANCE_ITEMS.filter(i => i.cat === cat)
    const [open, setOpen] = useState(false)

    return (
      <div style={{
        background:   '#fff',
        border:       `1.5px solid ${prog.pct === 100 ? '#5E8A72' : '#E2E4E7'}`,
        borderRadius: 12,
        overflow:     'hidden',
        marginBottom: 10,
      }}>
        <div onClick={() => setOpen(o => !o)} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', cursor:'pointer' }}>
          <div style={{ fontSize:22 }}>{c.icon}</div>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
              <span style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:17, fontWeight:600, color:'#151715' }}>{c.label}</span>
              <span style={{ fontSize:10, background: prog.pct === 100 ? '#EAF2EE' : '#EEEFF1', color: prog.pct === 100 ? '#5E8A72' : '#5C6065', fontWeight:700, padding:'2px 8px', borderRadius:20 }}>
                {prog.done}/{prog.total} {prog.pct === 100 ? '✅' : ''}
              </span>
            </div>
            <div style={{ height:3, background:'#E2E4E7', borderRadius:2, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${prog.pct}%`, background:'linear-gradient(90deg,#41427a,#4D4E8D)', borderRadius:2, transition:'width 0.4s ease' }} />
            </div>
          </div>
          <span style={{ color:'#A2A6AA', fontSize:12 }}>{open ? '▲' : '▼'}</span>
        </div>
        {open && (
          <div style={{ padding:'8px 12px 12px' }}>
            {items.map(item => (
              <div key={item.id} style={{ marginBottom:4 }}>
                <div style={{ fontSize:9, color:'#A2A6AA', letterSpacing:'0.08em', marginBottom:2, paddingLeft:30 }}>
                  {FREQUENCIES.find(f => f.id === item.freq)?.label.toUpperCase()}
                </div>
                <CheckItem item={item} />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const total = getTotalProgress()

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:200 }}><div className="spinner" /></div>

  return (
    <main tabIndex={-1} style={{ maxWidth:780, margin:'0 auto' }}>

      {/* ── HERO ── */}
      <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:14, padding:'20px 24px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:14 }}>
          <div>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, color:'#fff', fontWeight:600, marginBottom:4 }}>
              🛠️ Platform Maintenance
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', maxWidth:400 }}>
              Keep NSV running like a well-oiled machine. Your master checklist for platform health, security, and growth.
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:9, color:'rgba(255,255,255,0.4)', letterSpacing:'0.1em', marginBottom:2 }}>OVERALL PROGRESS</div>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#fff', fontWeight:700, lineHeight:1 }}>{total.pct}%</div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)' }}>{total.done}/{total.total} tasks</div>
          </div>
        </div>

        {/* Overall progress bar */}
        <div style={{ height:4, background:'rgba(255,255,255,0.1)', borderRadius:2, overflow:'hidden', marginBottom:12 }}>
          <div style={{ height:'100%', width:`${total.pct}%`, background:'linear-gradient(90deg,#41427a,#4D4E8D)', borderRadius:2, transition:'width 0.4s ease' }} />
        </div>

        {/* Frequency summary pills */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {FREQUENCIES.map(f => {
            const p = getFreqProgress(f.id)
            return (
              <div key={f.id} style={{
                background:   p.pct === 100 ? 'rgba(45,122,79,0.3)' : 'rgba(255,255,255,0.08)',
                border:       `1px solid ${p.pct === 100 ? 'rgba(45,122,79,0.5)' : 'rgba(255,255,255,0.15)'}`,
                borderRadius: 20,
                padding:      '4px 10px',
                fontSize:     10,
                color:        p.pct === 100 ? '#5BA882' : 'rgba(255,255,255,0.6)',
                fontWeight:   600,
              }}>
                {f.icon} {f.label}: {p.done}/{p.total}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── VIEW TOGGLE ── */}
      <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center' }}>
        <div style={{ fontSize:12, color:'#5C6065', marginRight:4 }}>View by:</div>
        {[
          { id:'frequency', label:'📅 Frequency' },
          { id:'category',  label:'📂 Category'  },
        ].map(v => (
          <button key={v.id} type="button" onClick={() => setView(v.id)}
            style={{
              fontSize:     12,
              fontWeight:   view === v.id ? 600 : 400,
              color:        view === v.id ? '#151715' : '#5C6065',
              background:   view === v.id ? '#fff' : 'transparent',
              border:       `1.5px solid ${view === v.id ? '#4D4E8D' : '#D7DADE'}`,
              borderRadius: 20,
              padding:      '5px 14px',
              cursor:       'pointer',
              fontFamily:   'inherit',
              transition:   'all 0.15s',
            }}>{v.label}</button>
        ))}
        <div style={{ marginLeft:'auto', fontSize:11, color: saving ? '#41427a' : '#A2A6AA' }}>
          {saving ? '⏳ Saving...' : lastSaved ? `✅ Saved ${lastSaved.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}` : 'Auto-saves as you check'}
        </div>
      </div>

      {/* ── FREQUENCY VIEW ── */}
      {view === 'frequency' && FREQUENCIES.map(f => (
        <FreqSection key={f.id} freq={f.id} />
      ))}

      {/* ── CATEGORY VIEW ── */}
      {view === 'category' && CATEGORIES.map(c => (
        <CatSection key={c.id} cat={c.id} />
      ))}

      {/* ── FOOTER NOTE ── */}
      <div style={{ marginTop:16, padding:'12px 16px', background:'#F7F8F9', border:'1px solid #E2E4E7', borderRadius:10, fontSize:11, color:'#5C6065', textAlign:'center' }}>
        🏥 A well-maintained platform is a trustworthy platform. <strong style={{ color:'#151715' }}>Big Network. Small Business Heart.</strong>
      </div>

    </main>
  )
}
